/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react"
import {
  BaseTable,
  Card,
  FooterMenu,
  HeaderMenu,
  Loading,
  NumberInput
} from "components"
import {
  AddReminder,
  Date,
  Deliver,
  Memo,
  PromisedDate,
  RecurringButton,
  Supplier,
  SupplierId,
  TableSection,
  TaxInclusive,
  TradingTerm
} from "../components"
import { ReminderContext } from "../contexts"
import { yupResolver } from "@hookform/resolvers/yup"
import { Controller, FormProvider, useForm } from "react-hook-form"
import toast from "react-hot-toast"
import { useLocation, useParams } from "react-router-dom"
import moment from "moment"
import {
  APPROVAL,
  approvalChecked,
  convertNumber,
  freightAlert,
  useApi,
  useTaxCode
} from "utils"
import { useGetListDetailBillService, useGetSingleBillService } from "./hooks"
import { bill_service_values, BillServiceValues, DetailBillServiceValues } from "./types"
import { BillServiceCode, BillServiceTableForm, TabBillService } from "./components"
import { DataBillContext, DataContext } from "../EnterBill/contexts"

type EditBillServiceProps = {
  disable?: boolean
  bill_id?: number | null
}

export const EditBillService = ({ disable, bill_id }: EditBillServiceProps) => {
  const api = useApi() 
  const { id } = useParams()
  const location = useLocation()
  const dataTaxCode = useTaxCode()
  const getSingleBillService = useGetSingleBillService(disable ? bill_id?.toString()! : id!)
  const dataSingle = getSingleBillService?.data
  
  const getDetail = useGetListDetailBillService({ bill_id: Number(id)! })

  const defaultValues: BillServiceValues = {
    trx_code: "",
    vendor_id: "",
    vendor_name: "",
    location_id: "",
    address: "",
    memo: "",
    transaction_date: moment().format("YYYY-MM-DD"),
    referensi: "",
    freight: 0,
    promise_date: moment().format("YYYY-MM-DD"),
    details: [],
    reminder: null,
  }
  const methods = useForm<BillServiceValues>({ defaultValues, resolver: yupResolver(bill_service_values) })

  useEffect(() => {
    if (dataSingle) {
      const newValues: BillServiceValues = {
        trx_code: dataSingle?.trx_code || "",
        vendor_id: String(dataSingle?.vendor_id) || "",
        vendor_name: "",
        location_id: String(dataSingle?.location_id) || "",
        address: dataSingle?.address || "",
        memo: dataSingle?.memo || "",
        transaction_date: dataSingle?.transaction_date || moment().format("YYYY-MM-DD"),
        referensi: dataSingle?.referensi || "",
        freight: parseFloat(dataSingle?.freight) || 0,
        promise_date: moment().format("YYYY-MM-DD"),
        details: dataSingle?.detail_bill?.length ? dataSingle?.detail_bill?.map(val => ({
          product_id: String(val?.product_id),
          description: String(val?.description),
          quantity_order: Number(val?.quantity_order),
          quantity_unit: String(val?.quantity_unit),
          price_bill: Number(val?.price_bill),
          discount: Number(val?.discount),
          total: Number(val?.total),
          project_id: String(val?.project_id),
          tax_id: String(val?.tax_id),
          tax_rate: String(val?.tax_rate),
          total_tax_paid: Number(val?.total_tax_paid),
          mr_unit_id: String(val?.mr_unit_id),
        })) : [],
        reminder: null,
      };

      methods.reset(newValues);
    }
  }, [dataSingle]);

  const onSubmit = (value: BillServiceValues) => {
    const finalPayload = { 
      ...value, 
      vendor_name: undefined, 
      reminder: undefined,
      payment_term: undefined,
      details: value?.details?.map(val => ({
        ...val,
        tax_rate: val?.tax_rate,
        total_tax_paid: val?.total_tax_paid,
        quantity_unit: val?.mr_unit_id,
      }))
    }

    return new Promise<void>((resolve) => {
      const submitForm = (): void => {
        toast.promise(api.put(`probill/update-bill-service/${id}`, finalPayload),
          {
            loading: "Loading...",
            success: (res) => res.data.message,
            error: (err) => err.response.data.detail?.message ?? err.response.data.message
          }
        )
        .then(() => {
          methods.reset()
          const overflowElement = document.querySelector('#overflow-container')
          overflowElement!.scrollTo({ top: 0, behavior: 'smooth' })
          getSingleBillService.refetch()
        })
        .catch(() => {})
        .finally(resolve)
      }

      if (!value.freight) {
        freightAlert("Bill Service")
        .then(() => submitForm())
        .catch(() => resolve())
      } else {
        submitForm()
      }
    })
  }

  const getSubtotalItem = (item: DetailBillServiceValues): number => item?.total!
  const detail = methods.watch("details")
  const freight = methods.watch("freight")

  const subtotal: number = detail.reduce((acc: number, item: DetailBillServiceValues): number => {
    return acc + getSubtotalItem(item)
  }, 0)
  const tax: number = detail.reduce((acc: number, item: DetailBillServiceValues): number => {
    const tax_item = dataTaxCode?.data?.find(i => i?.value === item?.tax_id)

    return acc + (getSubtotalItem(item) * (tax_item?.rate ?? 0) / 100)
  }, 0)
  const total = subtotal + freight + tax
  const totalPayment = getDetail?.data?.reduce((acc, item) => acc + (item.deposit_amount ?? 0) + (item.payment_amount ?? 0), 0) ?? 0
  const totalBalance = total - totalPayment

  const isEdit: boolean = dataSingle?.approval_status !== APPROVAL.APPROVED && dataSingle?.approval_status !== APPROVAL.REJECTED
  const isView: boolean = dataSingle?.approval_status === APPROVAL.APPROVED || dataSingle?.approval_status === APPROVAL.REJECTED
  const disableField = isView || disable

  useEffect(() => {
    const state = location.state

    if (state) {
      methods.setValue("vendor_id", state.vendor_id)
      methods.setValue("vendor_name", state._vendor_name)
    }

    // eslint-disable-next-line
  }, [location.state])

  if (getSingleBillService.isLoading || !getSingleBillService?.data) {
    return <Loading errorText="No data available" loading={getSingleBillService.isLoading} />
  }

  return (
    <FormProvider {...methods}>
      <section className="container my-5 flex flex-col gap-2">
        <HeaderMenu title="BILL SERVICES" className={disable ? "" : "sticky top-[30px] z-[2]"} action={disable ? "view" : isEdit ? "edit" : "view"} >
          <BillServiceCode code={dataSingle?.referensi} />
        </HeaderMenu>

        <section className="grid lg:grid-cols-3 gap-x-6 gap-y-3 items-end">
          <Supplier
            name="vendor_id"
            trading_term_name="payment_term"
            vendor_name="vendor_name"
            disabled={disableField}
          />

          <TradingTerm name="payment_term" />
          <TaxInclusive name="tax_inclusive" isHidden />
        </section>

        <Card>
          <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
            <section className="flex flex-col gap-3">
              <Deliver
                deliver_name="location_id"
                detail_name="address"
                disabled={disableField}
              />

              <Memo name="memo" disabled={disableField} />
            </section>

            <section className="flex flex-col gap-3">
              <Date name="transaction_date" disabled={disableField} />
              <SupplierId name="referensi" disabled={disableField} />
              <PromisedDate name="promize_date" disabled={disableField} />

              <ReminderContext.Provider
                value={{
                  disabled: disable ? true : isView ? true : false,
                  transaction_from: "BILL SERVICE",
                  name: {
                  memo: "memo",
                  promise_date: "promise_date",
                  reminder: "reminder",
                  referensi: "referensi",
                  transaction_date: "transaction_date",
                  vendor_name: "vendor_name",
                  reminder_child: {
                    memo: "reminder.memo",
                    promise_date: "reminder.promise_date",
                    transaction_date: "reminder.transaction_date"
                  }
                  },
                  onSuccess: (value) => {
                  methods.setValue("reminder.memo", value.memo)
                  methods.setValue("reminder.promise_date", value.promise_date)
                  methods.setValue("reminder.transaction_date", value.transaction_date)
                  }
                }}
              >
                <AddReminder />
              </ReminderContext.Provider>
            </section>
          </Card.Body>
        </Card>

        <BillServiceTableForm disable={disableField} />

        <section className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
          <section className="flex flex-col gap-3">
            <TableSection>
              <TableSection.Row label="BILL SERVICE STATUS" value={dataSingle?.bill_status ? String(dataSingle?.bill_status) === "1" ? "Open" : "Close" : "-"} />
              <TableSection.Row label="APPROVE STATUS" value={dataSingle?.approval_status ? approvalChecked(dataSingle?.approval_status) : "-"} />
              <TableSection.Row label="APPROVED BY" value={dataSingle?.approve_by_name ?? "-"} />
            </TableSection>

            <RecurringButton />
          </section>
            
          <section className="flex flex-col lg:items-end gap-3">
            <BaseTable
              plain
              className="w-full min-w-[100px] md:max-w-[400px] text-right"
            >
              <tbody>
                <tr>
                  <td className="w-[1px] whitespace-nowrap">SUBTOTAL</td>
                  <td>{convertNumber(subtotal).intoCurrency}</td>
                </tr>
                <tr>
                  <td className="w-[1px] whitespace-nowrap">FREIGHT</td>
                  <Controller
                    control={methods.control}
                    name="freight"
                    render={({ field, fieldState }) => (
                      <NumberInput
                        inputClass="text-right"
                        error={fieldState.error?.message}
                        ref={field.ref}
                        value={field.value ?? 0}
                        onValueChange={e => {
                          const value: number = e.floatValue ?? 0
                          field.onChange(value)
                        }}
                        disabled={isView || disable}
                      />
                    )}
                  />
                </tr>
                <tr>
                  <td className="w-[1px] whitespace-nowrap">TAX</td>
                  <td>{convertNumber(tax).intoCurrency}</td>
                </tr>
                <tr className="font-bold">
                  <td className="w-[1px] whitespace-nowrap">TOTAL AMOUNT</td>
                  <td>{convertNumber(total).intoCurrency}</td>
                </tr>
                <tr className="text-error">
                  <td className="w-[1px] whitespace-nowrap">PAYMENT</td>
                  <td>{convertNumber(totalPayment).intoCurrency}</td>
                </tr>
                <tr className="border-y-2 border-black text-lg font-bold">
                  <td className="w-[1px] whitespace-nowrap">BALANCE</td>
                  <td>{convertNumber(totalBalance).intoCurrency}</td>
                </tr>
              </tbody>
            </BaseTable>
          </section>
        </section>

        <DataBillContext.Provider value={{ 
          approval_status: "",
          approve_status: 0,
          approved_by: "",
          created_by: NaN,
          bill_status: dataSingle?.bill_status ? String(dataSingle?.bill_status) === "1" ? "Open" : "Close" : "",
          payment_list: getDetail?.data?.length ? getDetail?.data?.map(val => ({
            payment_bill_id: val?.payment_bill_id,
            trx_code: val?.trx_code,
            transaction_date: val?.transaction_date,
            approve_status: val?.approve_status,
            approve_date: val?.approve_date,
            ref_bill_id: val?.ref_bill_id,
            memo: val?.memo,
            deposit_amount: val?.deposit_amount,
            payment_amount: val?.payment_amount,
          })) : [],
          po_code: "",
          total_payment: totalPayment,
          refetch: getDetail?.refetch
        }}>
          <DataContext.Provider
            value={{
              _vendor_name: "",
              address: "",
              approval_status: dataSingle?.approval_status ?? 0,
              approve_by: 0,
              approve_by_name: "",
              created_by: 0,
              delivered: 0,
              freight: 0,
              location_id: "",
              memo: "",
              payment_term: "",
              po_delivery_status: 0,
              po_payment_status_text: "",
              podetail: [],
              promize_date: "",
              referensi: "",
              reminder: null,
              tax_inclusive: false,
              transaction_date: "",
              trx_code: "",
              vendor_id: "",
              po_status: 0,
              purchase_order_id: 0,
              sugest_freight: 0,
              po_payment_status: 0
            }}
          >
            <TabBillService />
          </DataContext.Provider>
        </DataBillContext.Provider>

        {!disableField && <FooterMenu
          hasCancelButton
          save={{
            permission: "PT021",
            actiontype: "save",
            type: "button",
            color: "primary",
            loading: methods.formState.isSubmitting ? "true" : undefined,
            onClick: methods.handleSubmit(onSubmit)
          }}
        />}
        </section>
    </FormProvider>
  )
}